import React, { useCallback, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { useQueryClient } from '@tanstack/react-query'
import { makeStyles } from '@material-ui/core'
import { useExplorerConfigurations, useDeleteExplorerConfiguration, useOrionIdentifier } from '../../../api/coreData'
import { useAppContext, useHideSideMenuEffect } from '../../../redux/slices/appContext'
import ExplorerConfigToolbar from './ExplorerConfigToolbar'
import LoadingMessage from './LoadingMessage'
import PortfolioExplorerGrid from './PortfolioExplorerGrid'
import PreferencesDialog from './PreferencesDialog'
import { useExplainRowHandler } from './ResearchAgentTray/hooks/useExplainRowHandler'
import { useExplorerConfig } from './hooks/useExplorerConfig'
import { useExperimentalFeature } from './hooks/useExperimentalFeature'
import { useExplorerConfigurationsForClient } from './hooks/useExplorerConfigurationsForClient'
import { usePortfolioExplorerQuery } from './hooks/usePortfolioExplorerQuery'
import { usePortfolioExplorerPreferences } from './hooks/usePortfolioExplorerPreferences'
import { compileScopeFilter } from './lib/scopeFilterCompile'
import { getLastViewId, setLastViewId, getRecentViews, pushRecentView } from './lib/viewStorage'
import { PortfolioDataProvider } from './utilityPanel/PortfolioDataProvider'
import UtilityPanel from './utilityPanel/UtilityPanel'
import { UtilityPanelProvider, useUtilityPanel } from './utilityPanel/UtilityPanelProvider'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    background: 'var(--opal-surface-default, #fff)'
  },
  workArea: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    minWidth: 0
  },
  loading: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: 100
  },
  empty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-500, #888)',
    fontSize: 14
  },
  error: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    color: 'var(--color-danger-600, #c62828)',
    fontSize: 14
  },
  errorDetail: {
    fontSize: 12,
    color: 'var(--color-neutral-500, #888)'
  }
}))

export default function PortfolioExplorer () {
  const { userId } = useAppContext()
  return (
    <UtilityPanelProvider userId={userId}>
      <PortfolioExplorerContent />
    </UtilityPanelProvider>
  )
}

function PortfolioExplorerContent () {
  const classes = useStyles()
  const { userId, clientId } = useAppContext()
  useHideSideMenuEffect([])

  const {
    config,
    columnDefinitions,
    setGroupings,
    setColumns,
    setQuickDate,
    setDateRange,
    setFetchAllLevels,
    setIncludeBenchmarks,
    setScopeFilters,
    addDefaultChart,
    removeDefaultChart,
    updateDefaultChart,
    setDefaultCharts,
    loadConfig,
    serializeConfig,
    isDirty,
    markClean
  } = useExplorerConfig()

  const [asOfDate, setAsOfDate] = useState(() => dayjs().subtract(1, 'day').format('YYYY-MM-DD'))

  // Subject = the scope all open charts re-pivot to. One of:
  //   null                                  -> Household (default)
  //   { kind: 'row',   row }                -> a specific grid entity
  //   { kind: 'level', depth, label }       -> all entities at a grouping depth
  //
  // Right-click flows, the row select handle, the breadcrumb segment
  // handle, and the panel header subject picker all funnel into this
  // single state. Tools read it from PortfolioDataProvider.
  const [selectedSubject, setSelectedSubject] = useState(null)
  const setSubjectRow = useCallback((row) => {
    setSelectedSubject(row ? { kind: 'row', row } : null)
  }, [])
  const toggleSubjectRow = useCallback((row) => {
    if (!row) { setSelectedSubject(null); return }
    setSelectedSubject(prev => (
      prev?.kind === 'row' && prev.row?._rowId === row._rowId
        ? null
        : { kind: 'row', row }
    ))
  }, [])
  const toggleSubjectLevel = useCallback((depth, label) => {
    if (depth == null) { setSelectedSubject(null); return }
    setSelectedSubject(prev => (
      prev?.kind === 'level' && prev.depth === depth
        ? null
        : { kind: 'level', depth, label: label || null }
    ))
  }, [])
  const setSubjectLevel = useCallback((depth, label) => {
    if (depth == null) { setSelectedSubject(null); return }
    setSelectedSubject({ kind: 'level', depth, label: label || null })
  }, [])
  const clearSelectedSubject = useCallback(() => setSelectedSubject(null), [])

  const { prefs, setPrefs } = usePortfolioExplorerPreferences(userId)
  // Scope filters (SMT-1608 Phase 1) are gated behind an experimental
  // opt-in. When the flag is off, the dialog hides Blade 3 and the query
  // hook strips `config.scopeFilters` from the effective config so no
  // `grouping.filter` is emitted on the Orion request -- stored filters
  // stay dormant in the saved view until the toggle is back on.
  const scopeFiltersEnabled = useExperimentalFeature('scopeFilters', prefs)
  // `hasActiveScopeFilters` is *only* true when (a) the experimental gate
  // is on and (b) at least one stored filter actually compiles to a
  // non-null value on the wire. Compiling here (rather than checking
  // `v != null` on the raw tree) avoids a false-positive asterisk for
  // predicates the user added but never gave a value to -- those compile
  // to null and don't emit a `grouping.filter`, so the household total
  // is genuinely unfiltered, and no caveat is needed. `compileScopeFilter`
  // is pure and cheap; running it once per grouping per render is
  // negligible.
  const hasActiveScopeFilters = scopeFiltersEnabled &&
    Object.values(config.scopeFilters || {}).some((v) => compileScopeFilter(v) != null)

  const {
    rows,
    totalRow,
    isFetching,
    error,
    refetch,
    expandRow,
    expandToLevelLazy,
    expandAllChildrenLazy,
    lazyExpandState,
    cancelLazyExpand,
    hasClient,
    isReady,
    request: orionRequest
  } = usePortfolioExplorerQuery(config, asOfDate, { scopeFiltersEnabled })

  const { data: savedConfigs, refetch: refetchConfigs } = useExplorerConfigurations()
  // The bucket hook reads the same React-Query cache as
  // useExplorerConfigurations above, so this is a derived view (no extra
  // network call). We only need the `thisClient` slice here for the
  // first-load ladder; the toolbar and dialogs consume the hook
  // independently.
  const { thisClient } = useExplorerConfigurationsForClient(clientId)
  const deleteMutation = useDeleteExplorerConfiguration()

  const [activeConfigId, setActiveConfigId] = useState(null)
  const [activeViewName, setActiveViewName] = useState(null)
  const [recentViews, setRecentViews] = useState(() => getRecentViews(userId))
  const defaultLoadedRef = useRef(false)

  const [prefsDialogOpen, setPrefsDialogOpen] = useState(false)
  const openPrefs = useCallback(() => setPrefsDialogOpen(true), [])
  const closePrefs = useCallback(() => setPrefsDialogOpen(false), [])

  const {
    isOpen: panelOpen,
    openPanel,
    openExclusive,
    effectivePreset,
    customRatio
  } = useUtilityPanel()

  const handleExplainRow = useExplainRowHandler({
    openExclusive,
    clientId,
    config,
    activeViewName,
    asOfDate,
    orionRequest
  })

  // Identity for tools that fire their own Orion requests (e.g. the
  // allocation tool's virtual-grouping queries). Same hook the main
  // query uses, so the cache is shared.
  const { data: orionIdentity } = useOrionIdentifier(clientId)

  const portfolioDataValue = {
    config,
    asOfDate,
    scopeFiltersEnabled,
    columnDefinitions,
    groupings: config.groupings,
    prefs,
    clientId,
    entity: orionIdentity?.entity,
    entityIds: orionIdentity?.entityIds,
    orionRequest,
    addDefaultChart,
    removeDefaultChart,
    updateDefaultChart,
    setDefaultCharts,
    selectedSubject,
    setSubjectRow,
    setSubjectLevel,
    toggleSubjectLevel,
    clearSelectedSubject
  }

  // Right-click flows open the tool in the EXCLUSIVE overlay (the
  // "old tool window" with chart + detail table), distinct from the
  // composable chart tiles in the stack. The stack hosts curated
  // catalog charts the user saves to the view; right-click is an
  // ad-hoc, single-shot drill-in. Subject is set so the exclusive
  // tool scopes to the right entity, and the stack reflects the same
  // selection when the user closes back to it.
  const handleViewAllocation = useCallback((payload) => {
    if (payload?.row) setSubjectRow(payload.row)
    openExclusive('allocation', payload)
  }, [openExclusive, setSubjectRow])

  const handleViewTrend = useCallback((payload) => {
    if (payload?.row) setSubjectRow(payload.row)
    openExclusive('trend', payload)
  }, [openExclusive, setSubjectRow])

  const handleCompareMeasures = useCallback((payload) => {
    if (payload?.row) setSubjectRow(payload.row)
    openExclusive('scatter', payload)
  }, [openExclusive, setSubjectRow])

  // Run = "refresh everything I'm looking at". The grid query is
  // `enabled: false` and only fetches on explicit refetch, but every
  // utility-panel tool query (allocation, trend, scatter) lives under
  // the same `['orion/domain-query', ...]` key namespace with its own
  // request body. Without invalidating that namespace, the tool
  // charts would keep serving cached responses keyed on their own
  // request bodies even though the user clearly meant to refetch --
  // most visibly when Orion-side data changed (overnight ETL, price
  // refresh) but the request body itself didn't. invalidateQueries
  // both refetches every currently-active query under the namespace
  // and marks the rest stale so they refetch on next mount.
  const queryClient = useQueryClient()
  const handleRun = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['orion/domain-query'] })
    refetch()
    // If the view has saved charts, pop the panel open on Run so the
    // user immediately sees them refresh. Idempotent when already open.
    if ((config.defaultCharts?.length || 0) > 0) {
      openPanel()
    }
  }, [queryClient, refetch, config.defaultCharts, openPanel])

  // Auto-open the panel when a saved view with charts is loaded so the
  // user doesn't have to dig for them. Keyed on `activeConfigId` so it
  // only fires on view-load (not when the user manually adds a chart
  // from the toolbar while the panel is closed).
  useEffect(() => {
    if (!activeConfigId) return
    if ((config.defaultCharts?.length || 0) > 0) {
      openPanel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConfigId])

  // Cmd+, (macOS) / Ctrl+, (other platforms) opens Preferences. Skip when focus
  // is in a text input — typing in a dialog field shouldn't pop another dialog.
  useEffect(() => {
    const isEditableTarget = (target) => {
      if (!target) return false
      const tag = target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      if (target.isContentEditable) return true
      return false
    }
    const onKeyDown = (e) => {
      // Cmd+Shift+. (mac) / Ctrl+Shift+. (other). The plain Cmd+, alternative
      // is captured by Chrome on macOS for chrome://settings, so the page
      // handler never fires there. Cmd+Shift+. avoids every browser-level
      // shortcut while still feeling like a "preferences" combo. Using
      // e.code ('Period') instead of e.key keeps this layout-independent --
      // Shift+. produces '>' on US layouts and the printable char varies on
      // others.
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey) return
      if (e.code !== 'Period') return
      if (isEditableTarget(e.target)) return
      e.preventDefault()
      setPrefsDialogOpen(prev => !prev)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    // First-load ladder, evaluated once per mount via `defaultLoadedRef`:
    //   1. The last view this user touched (across all clients) -- keeps
    //      mid-session refresh from yanking the advisor out of their work.
    //   2. The lowest-ordinal view saved for the current client (the
    //      per-client default introduced by client-scoped saved views).
    //   3. The user's legacy global default (still in place until the
    //      explorerConfigurations.isDefault column is retired).
    //   4. Nothing -- leave the explorer empty.
    if (savedConfigs && !defaultLoadedRef.current) {
      defaultLoadedRef.current = true

      const lastId = getLastViewId(userId)
      const lastConfig = lastId ? savedConfigs.find(c => c.explorerConfigurationId === lastId) : null

      if (lastConfig) {
        loadConfig(lastConfig)
        setActiveConfigId(lastConfig.explorerConfigurationId)
        setActiveViewName(lastConfig.name)
        return
      }

      const clientDefault = thisClient[0]
      if (clientDefault) {
        loadConfig(clientDefault)
        setActiveConfigId(clientDefault.explorerConfigurationId)
        setActiveViewName(clientDefault.name)
        setLastViewId(userId, clientDefault.explorerConfigurationId)
        pushRecentView(userId, clientDefault.explorerConfigurationId, clientDefault.name)
        setRecentViews(getRecentViews(userId))
        return
      }

      const defaultConfig = savedConfigs.find(c => c.isDefault)
      if (defaultConfig) {
        loadConfig(defaultConfig)
        setActiveConfigId(defaultConfig.explorerConfigurationId)
        setActiveViewName(defaultConfig.name)
        setLastViewId(userId, defaultConfig.explorerConfigurationId)
        pushRecentView(userId, defaultConfig.explorerConfigurationId, defaultConfig.name)
        setRecentViews(getRecentViews(userId))
      }
    }
  }, [savedConfigs, thisClient, loadConfig, userId])

  const handleLoadConfig = useCallback((savedConfig) => {
    loadConfig(savedConfig)
    setActiveConfigId(savedConfig.explorerConfigurationId)
    setActiveViewName(savedConfig.name)
    setLastViewId(userId, savedConfig.explorerConfigurationId)
    pushRecentView(userId, savedConfig.explorerConfigurationId, savedConfig.name)
    setRecentViews(getRecentViews(userId))
  }, [loadConfig, userId])

  const handleSaveComplete = useCallback(({ explorerConfigurationId, name }) => {
    if (explorerConfigurationId != null) {
      setActiveConfigId(explorerConfigurationId)
      setLastViewId(userId, explorerConfigurationId)
      pushRecentView(userId, explorerConfigurationId, name)
      setRecentViews(getRecentViews(userId))
    }
    setActiveViewName(name)
    markClean()
  }, [markClean, userId])

  const handleDeleteConfig = useCallback((configId) => {
    deleteMutation.mutate(
      { explorerConfigurationId: configId },
      {
        onSuccess: () => {
          if (activeConfigId === configId) {
            setActiveConfigId(null)
            setActiveViewName(null)
            setLastViewId(userId, null)
          }
        }
      }
    )
  }, [deleteMutation, activeConfigId, userId])

  const canRun = hasClient && config.columns.length > 0 && isReady
  // `totalRow` is populated as soon as the response arrives, even when the
  // child rows list is empty (e.g. a scope filter excluded every account).
  // Treat the presence of either rows or a totalRow as "we got data" so the
  // user sees the grid + rollups instead of a "Run" placeholder when their
  // filter legitimately matched nothing.
  const hasResults = rows.length > 0 || totalRow != null || error

  // When the panel is open, the work-area's flex budget is split between
  // the grid column (`contentFlex`) and the panel (`panelFlex`). When
  // closed, the grid takes the whole row.
  const contentFlexValue = panelOpen ? effectivePreset.contentFlex : 1
  const contentTransition = customRatio ? 'none' : undefined

  return (
    <PortfolioDataProvider value={portfolioDataValue}>
      <div className={classes.root}>
        <ExplorerConfigToolbar
          config={config}
          setGroupings={setGroupings}
          setColumns={setColumns}
          setQuickDate={setQuickDate}
          setDateRange={setDateRange}
          setFetchAllLevels={setFetchAllLevels}
          setIncludeBenchmarks={setIncludeBenchmarks}
          setScopeFilters={setScopeFilters}
          scopeFiltersEnabled={scopeFiltersEnabled}
          isFetching={isFetching}
          onRefresh={handleRun}
          canRun={canRun}
          savedConfigs={savedConfigs || []}
          activeConfigId={activeConfigId}
          activeViewName={activeViewName}
          isDirty={isDirty}
          onLoadConfig={handleLoadConfig}
          onDeleteConfig={handleDeleteConfig}
          onRefreshConfigs={refetchConfigs}
          serializeConfig={serializeConfig}
          onSaveComplete={handleSaveComplete}
          recentViews={recentViews}
          asOfDate={asOfDate}
          setAsOfDate={setAsOfDate}
          onOpenPrefs={openPrefs}
        />

        <div className={classes.workArea}>
          <div
            className={classes.content}
            style={{ flex: contentFlexValue, transition: contentTransition }}
          >
            {!hasClient && (
              <div className={classes.empty}>
                <p>Select a client to explore portfolio data.</p>
              </div>
            )}

            {hasClient && config.columns.length === 0 && (
              <div className={classes.empty}>
                <p>Select at least one column to display data.</p>
              </div>
            )}

            {canRun && isFetching && (
              <div className={classes.loading}>
                <LoadingMessage active={isFetching} />
              </div>
            )}

            {hasClient && error && !isFetching && (
              <div className={classes.error}>
                <p>Failed to load portfolio data.</p>
                <p className={classes.errorDetail}>{error.message || 'Unknown error'}</p>
              </div>
            )}

            {canRun && !isFetching && !hasResults && (
              <div className={classes.empty}>
                <p>Configure your view and click Run.</p>
              </div>
            )}

            {canRun && !isFetching && !error && (rows.length > 0 || totalRow != null) && (
              <PortfolioExplorerGrid
                rows={rows}
                columnDefinitions={columnDefinitions}
                columnInstances={config.columns}
                totalRow={totalRow}
                onExpandRow={expandRow}
                onExplainRow={handleExplainRow}
                onViewAllocation={handleViewAllocation}
                onViewTrend={handleViewTrend}
                onCompareMeasures={handleCompareMeasures}
                selectedRowId={selectedSubject?.kind === 'row' ? (selectedSubject.row?._rowId ?? null) : null}
                onSelectRow={toggleSubjectRow}
                selectedLevelDepth={selectedSubject?.kind === 'level' ? selectedSubject.depth : null}
                onSelectLevel={toggleSubjectLevel}
                groupings={config.groupings}
                expandToLevelLazy={expandToLevelLazy}
                expandAllChildrenLazy={expandAllChildrenLazy}
                lazyExpandState={lazyExpandState}
                cancelLazyExpand={cancelLazyExpand}
                viewName={activeViewName}
                asOfDate={asOfDate}
                prefs={prefs}
                hasActiveScopeFilters={hasActiveScopeFilters}
              />
            )}
          </div>

          <UtilityPanel />
        </div>

        <PreferencesDialog
          open={prefsDialogOpen}
          onClose={closePrefs}
          prefs={prefs}
          setPrefs={setPrefs}
        />
      </div>
    </PortfolioDataProvider>
  )
}
